/** @jsxImportSource @emotion/react */

import { CalciteLoader } from "@esri/calcite-components-react";
import "@esri/calcite-components/dist/calcite/calcite.css";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactTabulator } from "react-tabulator";
import RecordService from "services/RecordService";
import tw from "twin.macro";
import { formatModifiedBy, formatPhoneNumber } from "utils/helpers";
import { Button } from "../components/Button";
import { Card, CardHeader } from "../components/Card";
import { Input } from "../components/Input";
import { Page } from "../components/Page";

import NenaIDSelect, { MigrateForm } from "components/NenaIDSelect";
import { NewCPNInput } from "components/NewCPNInput";
import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import "react-tabulator/lib/styles.css";
import { ALIRecordRequest } from "services/ALIRecord";

const client = new RecordService("ali");

// Add custom CSS for ALI changes table
const aliChangesTableStyle = `
  #aliChangesLoader .tabulator {
    margin-bottom: 0 !important;
  }
  #aliChangesLoader .tabulator-footer {
    padding: 2px !important;
    margin: 0 !important;
    min-height: 0 !important;
    height: auto !important;
  }
  #aliChangesLoader .tabulator-footer #record-count {
    padding: 0 !important;
    margin: 0 0 0 5px !important;
    line-height: 1 !important;
    font-size: 0.875rem !important;
  }
`;

function QuickSearch() {
  const [formData, setFormData] = useState<{
    telephone1: string;
    telephone2: string;
    customer: string;
  }>({ telephone1: "", telephone2: "", customer: "" });
  const navigate = useNavigate();
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let phone1 = formData.telephone1.replace(/\D/g, "");
    let phone2 = formData.telephone2.replace(/\D/g, "");
    const criterias: { field: string; type: string; value: string }[] = [];

    if (!phone1 && phone2) {
      phone1 = "0000000000";
    }

    if (phone1 && !phone2) {
      phone2 = "9999999999";
    }

    if (phone1 || phone2) {
      criterias.push({
        field: "CPN",
        type: "BETWEEN",
        value: `${phone1},${phone2}`,
      });
    }

    if (formData.customer) {
      criterias.push({ field: "NAM", type: "like", value: formData.customer });
    }
    localStorage.setItem("aliRecords-criteria", JSON.stringify(criterias));
    navigate("/ali-records");
  };

  const handleChange = (e) => {
    let target = e.target as HTMLInputElement;
    if (target.type === "tel") {
      const phone = formatPhoneNumber(target.value);
      target.value = phone;
    }
    setFormData({ ...formData, [target.name]: target.value });
  };

  const handleOnkeyDown = (e: React.KeyboardEvent<HTMLInputElement | any>) => {
    if (e.key === "Tab") {
      if (formData.telephone1) {
        setFormData((prev) => ({
          ...prev,
          telephone2: prev.telephone1,
        }));
        setTimeout(() => {
          const tel2Input = document.querySelector(
            'input[name="telephone2"]',
          ) as HTMLInputElement | null;
          if (tel2Input) {
            tel2Input.setSelectionRange(
              tel2Input.value.length,
              tel2Input.value.length,
            );
            tel2Input.focus();
          }
        }, 0);
      }
    }
  };

  return (
    <form method="post" encType="" onSubmit={handleSubmit}>
      <div css={[tw`flex flex-col`]}>
        <div css={[tw`flex lg:flex-row flex-col gap-x-6 w-full`]}>
          <Input
            icon="phone"
            minLength={10}
            value={formData.telephone1}
            name="telephone1"
            type="tel"
            label="Telephone Range Start"
            max={14}
            required={false}
            onChange={(e) => handleChange(e)}
            onKeyDown={(e) => handleOnkeyDown(e)}
            placeholder="(XXX) XXX-XXXX"
            focused
          />
          <Input
            icon="phone"
            value={formData.telephone2}
            name="telephone2"
            type="tel"
            label="Telephone Range End"
            required={false}
            minLength={10}
            onChange={(e) => handleChange(e)}
            placeholder="(XXX) XXX-XXXX"
          />
        </div>
      </div>
      <div css={[tw`flex lg:flex-row flex-col gap-x-3 w-full items-end`]}>
        <Input
          wrapperCss={[tw`lg:w-3/5 w-full`]}
          icon="user"
          scale="m"
          value={formData.customer}
          name="customer"
          label="Customer Name"
          onChange={(e) => handleChange(e)}
          placeholder="Name"
        />
        <Button type="submit" width="auto" css={[tw`ml-auto mb-3`]}>
          Search
        </Button>
      </div>
    </form>
  );
}

function Dashboard() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [newTelephone, setNewTelephone] = useState<ALIRecordRequest>(
    {} as ALIRecordRequest,
  );

  const [migrateForm, setMigrateForm] = useState<MigrateForm>({
    CPF: "",
    CPN: "",
  });
  const [pageSize, setPageSize] = useState<number>(25);
  const [isTableReady, setIsTableReady] = useState(false);
  const tableRef = useRef<any>(null);

  useEffect(() => {
    setIsTableReady(true);
  }, []);

  // Changes table columns
  const changesColumns = [
    {
      title: "Telephone Number",
      field: "CPN",
      formatter: (cell) => formatPhoneNumber(cell.getValue()),
      width: 150,
      // minWidth: 120,
      headerSort: false,
    },
    {
      title: "NENA ID",
      field: "CPF",
      widthGrow: 1,
      headerSort: false,
    },
    {
      title: "Change",
      field: "FOC",
      widthGrow: 1,
      headerSort: false,
      hozAlign: "center" as const,
    },
    {
      title: "Last Modified",
      field: "last_modified",
      widthGrow: 2,
      headerSort: false,
    },
    {
      title: "Modified By",
      field: "modified_by",
      formatter: (cell) => formatModifiedBy(cell.getData()),
      widthGrow: 2,
      headerSort: false,
    },
  ];

  // Changes table options
  const changesOptions = {
    layout: "fitColumns",
    movableColumns: true,
    placeholder: "No Changes Found",
    persistenceID: "aliChanges1",
    footerElement: "<span id='record-count'>Showing Last 10 Changes</span>",
    ajaxRequestFunc: function (url, config, params) {
      let reqBody = {
        range: "0,10",
        sortBy: "last_modified",
        sortDirection: "desc",
        criterias: [],
        full: true,
      };
      // log request body
      console.log("ALI changes table request body", reqBody);
      return new Promise(function (resolve, reject) {
        // fetch records
        client
          .getRecords(reqBody, url)
          .then((res) => {
            // success -> return data
            resolve(res.data);
          })
          .catch((err) => {
            // error -> return error message
            console.error("ALI changes table error response", err);
            reject(err);
          });
      });
    },
    ajaxURL: "searchByCriteria",
    ajaxResponse: (url, params, response) => {
      console.log("ALI changes table response", response);
      let d = response.message.size > 0 ? response.message.list : [];
      return d;
    },
  };

  const handleMigrateChange = (e) => {
    let target = e.target as HTMLInputElement;
    if (target.name === "migrate-tel") {
      const phone = formatPhoneNumber(target.value);
      target.value = phone;
      setMigrateForm({ ...migrateForm, CPN: phone });
    } else {
      setMigrateForm({ ...migrateForm, CPF: target.value });
    }
  };

  const handleMigrateSubmit = () => {
    navigate("/ali-records/migrate", {
      state: {
        CPN: migrateForm.CPN,
        CPF: migrateForm.CPF,
      },
    });
  };

  const handleNewTelSubmit = () => {
    navigate("/ali-records/new", { state: { CPN: newTelephone.CPN } });
  };

  return (
    <>
      <style>{aliChangesTableStyle}</style>
      <Page title="Dashboard" label="Dashboard">
        <div css={[tw`flex gap-6`]}>
          <div css={[tw`flex-1 max-w-md`]}>
            <CardHeader
              style={{
                marginBottom: "10px",
              }}
            >
              Quick Start
            </CardHeader>
            {user?.functions?.includes("foresite:ali:view") && (
              <div css={[tw`flex-1`]}>
                <Card css={[tw`drop-shadow-sm border border-gray-200`]}>
                  <CardHeader>ALI Search</CardHeader>
                  <QuickSearch />
                </Card>
              </div>
            )}

            {user?.functions?.includes("foresite:ali:write") && (
              <>
                <div css={[tw`flex-1`]}>
                  <Card css={[tw`drop-shadow-sm border border-gray-200`]}>
                    <CardHeader>New Telephone Number</CardHeader>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleNewTelSubmit();
                      }}
                    >
                      <div
                        css={[
                          tw`flex lg:flex-row flex-col lg:items-start gap-x-3`,
                        ]}
                      >
                        <NewCPNInput
                          recordRequest={newTelephone}
                          setRecordRequest={setNewTelephone}
                          icon={true}
                          size="m"
                          placeholder={true}
                          key="dashboard-new-tel-input"
                        />
                        <Button
                          type="submit"
                          width="auto"
                          css={[tw`lg:mt-6 mb-3 ml-auto flex-shrink-0`]}
                        >
                          Add
                        </Button>
                      </div>
                    </form>
                  </Card>
                </div>

                <div css={[tw`flex-1`]}>
                  <Card css={[tw`drop-shadow-sm border border-gray-200`]}>
                    <CardHeader>Migrate Telephone Number</CardHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleMigrateSubmit();
                      }}
                    >
                      <div
                        css={[
                          tw`flex lg:flex-row flex-col lg:items-end gap-x-3`,
                        ]}
                      >
                        <Input
                          icon="phone"
                          value={migrateForm.CPN}
                          name="migrate-tel"
                          type="tel"
                          label="Telephone Number"
                          onChange={handleMigrateChange}
                          wrapperCss={[tw`lg:w-1/2`]}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        <NenaIDSelect
                          recordRequest={migrateForm}
                          setRecordRequest={setMigrateForm}
                          dashboard
                        />
                        <Button
                          type="submit"
                          width="auto"
                          css={[tw`mb-3 ml-auto flex-shrink-0`]}
                        >
                          Migrate
                        </Button>
                      </div>
                    </form>
                  </Card>
                </div>
              </>
            )}
          </div>

          {user?.functions?.includes("foresite:ali:view") && (
            <div css={[tw`flex-1`]}>
              <CardHeader css={[tw`ml-4`]}>Last Changes</CardHeader>
              <Card>
                <div css={[tw` min-h-full border border-gray-300 rounded-md`]}>
                  {isTableReady ? (
                    <ReactTabulator
                      id="aliChangesLoader"
                      onRef={(r) => {
                        tableRef.current = r.current;
                      }}
                      events={{
                        pageSizeChanged: function (pagesize) {
                          setPageSize(pagesize);
                        },
                      }}
                      options={changesOptions}
                      columns={changesColumns}
                    />
                  ) : (
                    <div css={[tw`flex justify-center items-center h-32`]}>
                      <CalciteLoader scale="s" label="Loading changes..." />
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </Page>
    </>
  );
}

export default Dashboard;
